// src/components/Groups/InviteUsersModal.jsx
import React, { useState } from 'react';
import { X, UserPlus, RefreshCw, Check, AlertTriangle } from 'lucide-react';
import { groupsAPI } from '../../api/groupsApi';
import UserSearchInput from './UserSearchInput';
import './InviteUsersModal.css';

const ROLE_HIERARCHY = { viewer: 0, editor: 1, admin: 2, owner: 3 };
const ALL_ROLES = ['viewer', 'editor', 'admin'];

const InviteUsersModal = ({ groupId, group, existingMemberIds = [], onClose, onSuccess }) => {
  const defaultRole     = group?.default_member_role || 'viewer';
  const groupVisibility = group?.visibility || 'private';

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [role,          setRole]          = useState(defaultRole);
  const [message,       setMessage]       = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [result,        setResult]        = useState(null); // { invited, skipped }

  const isRoleDisabled = (r) =>
    groupVisibility === 'public' &&
    ROLE_HIERARCHY[r] < ROLE_HIERARCHY[defaultRole];

  const handleAddUser = (user) => {
    if (existingMemberIds.includes(user.id)) return;
    setSelectedUsers((prev) =>
      prev.find((u) => u.id === user.id) ? prev : [...prev, user]
    );
  };

  const handleRemoveUser = (user) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
  };

  const handleSend = async () => {
    if (selectedUsers.length === 0) {
      setError('Select at least one user to invite.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await groupsAPI.inviteUsers(groupId, {
        user_ids:      selectedUsers.map((u) => u.id),
        role,
        message,
        expires_in_days: expiresInDays,
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to send invitations.');
    } finally {
      setLoading(false);
    }
  };

  // ── Result screen ─────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="ium__backdrop" onClick={onClose}>
        <div className="ium__modal" onClick={(e) => e.stopPropagation()}>
          <div className="ium__header">
            <h2 className="ium__title">Invitations Sent</h2>
            <button className="ium__close" onClick={onClose}><X size={18} /></button>
          </div>

          <div className="ium__result">
            {result.invited?.length > 0 && (
              <div className="ium__result-group ium__result-group--success">
                <p className="ium__result-label">
                  <Check size={15} /> {result.invited.length} invitation{result.invited.length !== 1 ? 's' : ''} sent
                </p>
                {result.invited.map((inv) => (
                  <div key={inv.id} className="ium__result-row">
                    <span>{inv.user_details?.full_name || inv.user_details?.email || inv.email}</span>
                    <span className="ium__result-role">{inv.role}</span>
                  </div>
                ))}
              </div>
            )}

            {result.skipped?.length > 0 && (
              <div className="ium__result-group ium__result-group--warn">
                <p className="ium__result-label">
                  <AlertTriangle size={15} /> {result.skipped.length} skipped
                </p>
                {result.skipped.map((s, i) => (
                  <div key={i} className="ium__result-row">
                    <span>{s.user_id}</span>
                    <span className="ium__result-reason">{s.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="ium__footer">
            <button className="btn btn--primary" onClick={onSuccess}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Compose screen ────────────────────────────────────────────────────────
  return (
    <div className="ium__backdrop" onClick={onClose}>
      <div className="ium__modal" onClick={(e) => e.stopPropagation()}>

        <div className="ium__header">
          <h2 className="ium__title"><UserPlus size={18} /> Invite Members</h2>
          <button className="ium__close" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ium__body">

          {error && (
            <div className="ium__error">
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          {/* User search */}
          <div className="ium__field">
            <label className="ium__label">Search & add people</label>
            <UserSearchInput
              selectedUsers={selectedUsers}
              onAdd={handleAddUser}
              onRemove={handleRemoveUser}
              excludeIds={existingMemberIds}
            />
          </div>

          {selectedUsers.length > 0 && (
            <>
              {/* Role selector */}
              <div className="ium__field">
                <label className="ium__label">
                  Invite as
                  {groupVisibility === 'public' && (
                    <span className="ium__label-hint">
                      (min: <strong>{defaultRole}</strong> — group default)
                    </span>
                  )}
                </label>
                <div className="ium__roles">
                  {ALL_ROLES.map((r) => {
                    const disabled = isRoleDisabled(r);
                    return (
                      <button
                        key={r}
                        type="button"
                        className={`ium__role-btn ${role === r ? 'ium__role-btn--active' : ''} ${disabled ? 'ium__role-btn--disabled' : ''}`}
                        onClick={() => !disabled && setRole(r)}
                        disabled={disabled}
                        title={disabled ? `Cannot invite below group default (${defaultRole})` : undefined}
                      >
                        {r}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Expiry */}
              <div className="ium__field ium__field--row">
                <label className="ium__label">Expires in</label>
                <select
                  className="ium__select"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(Number(e.target.value))}
                >
                  {[3, 7, 14, 30].map((d) => (
                    <option key={d} value={d}>{d} days</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="ium__field">
                <label className="ium__label">Personal message (optional)</label>
                <textarea
                  className="ium__textarea"
                  rows={2}
                  maxLength={500}
                  placeholder="Add a note to the invitation…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <span className="ium__char-count">{message.length}/500</span>
              </div>
            </>
          )}
        </div>

        <div className="ium__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSend}
            disabled={loading || selectedUsers.length === 0}
          >
            {loading
              ? <><RefreshCw size={14} className="spinning" /> Sending…</>
              : <><UserPlus size={14} /> Send {selectedUsers.length > 0 ? `(${selectedUsers.length})` : ''}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUsersModal;
